import {
  HeaderMenu,
  MainMenuInUse,
  QueryLocationGroup,
  QueryLocationMainMenu,
  QueryQcBizDict,
  QuerySubMenu,
  QueryTenant,
  Room,
  SubMenuInUse,
} from "@/modules/admin/objects/hotelAdmin";
import { i18n } from "@/framework/plugins/i18n";
import { Tenant } from "@/modules/staff/objects/Tenant";

export const NONE_ROOM_ID = -2;
export const UNASSIGNED_ROOM_ID = -1;
export const UNASSIGNED_GROUP_ID = -1;
export const SAVE_SUCCEEDED = "success";
export const SAVE_ACCEPTED = "accepted";

export const SUB_MENU_TYPE_FACILITY = "facility";
export const SUB_MENU_TYPE_COUPON = "coupon";
export const SUB_MENU_TYPE_FLOOR_GUIDE = "floor_guide";
export const SUB_MENU_TYPE_ANNOUNCEMENT = "announcement";
export const SUB_MENU_TYPE_QUESTIONNAIRE = "questionnaire";
export const SUB_MENU_TYPE_CONGESTION = "congestion";
export const SUB_MENU_TYPE_VOICE_TALK = "voice_talk";
export const SUB_MENU_TYPE_QR_ORDER = "qr_order";
export const SUB_MENU_TYPE_URL = "url";
export const SUB_MENU_TYPE_ORDER = "order";
export const SUB_MENU_TYPE_ONLINE_PAYMENT = "online_payment";
export const SUB_MENU_TYPE_AI_CHATBOT = "ai_chatbot";
export const SUB_MENU_TYPE_GUEST_SMS_CHAT = "guest_sms_chat";
export const MAIN_MENU_QUESTIONNAIRE_DICT_CD = "main_questionnaire";
export const ROOM_CONFIRMATION_TIMING_FIRST = "first";

// i.e. is of a menu type that needs a group assignment
export const staffGroupSubMenuLevel = (subMenuType: string): boolean => {
  return (
    subMenuType !== SUB_MENU_TYPE_FACILITY &&
    subMenuType !== SUB_MENU_TYPE_COUPON &&
    subMenuType != SUB_MENU_TYPE_QUESTIONNAIRE &&
    subMenuType !== SUB_MENU_TYPE_CONGESTION &&
    subMenuType !== SUB_MENU_TYPE_URL &&
    subMenuType !== SUB_MENU_TYPE_QR_ORDER
  );
};

export const getDefaultLocationGroup = (locationGroups: QueryLocationGroup[]): QueryLocationGroup => {
  let defaultGroup = locationGroups.filter((x) => x.is_default == true);
  // combines old logic as a fallback while preferring new logic of multiple location groups
  return defaultGroup.length > 0 ? defaultGroup[0] : locationGroups[0];
};

// Entirely for the purpose of running tests- see the specification at hotelAdmin.spec.ts
export const refineLocationCSV = (data: string, rooms: Room[]): Room[] => {
  const lines: string[] = data.split("\n");
  return lines
    .map((e) => e.trim())
    .filter((e, i, self) => self.indexOf(e) == i)
    .filter((e) => !!e.trim() && e.length < 200 && !rooms.find((room) => room.name === e.trim()))
    .map((e) => ({
      id: UNASSIGNED_ROOM_ID,
      name: e.trim(),
      qr: "",
      hash_value: "",
      location_group_id: UNASSIGNED_ROOM_ID,
      location_group_name: "",
    }));
};

export const setOrderNo = (target: Map<string, MainMenuInUse>) => {
  const setSubMenuOrderNo = (subMenu: Map<string, SubMenuInUse>) => {
    const subMenus = Array.from(target.values());
    for (let j = 0; j < subMenus.length; j++) {
      subMenus[j].orderNo = j;
    }
  };

  const mainMenus = Array.from(target.values());
  for (let i = 0; i < mainMenus.length; i++) {
    mainMenus[i].orderNo = i;
    // setSubMenuOrderNo(mainMenus[i].subMenus);
  }
};

export const isSaveSucceeded = (message?: string) => message === SAVE_SUCCEEDED;
export const isSaveAccepted = (message?: string) => message === SAVE_ACCEPTED;

export const makeMapOfMainMenuInUse = (
  mainMenuInUseMap: Map<string, MainMenuInUse>,
  tenant: QueryTenant,
  locationMainMenus: QueryLocationMainMenu[],
  subMenusShared: Map<string, SubMenuInUse>,
  favoriteSubMenus: Map<string, number[]>
): Map<string, MainMenuInUse> => {
  return locationMainMenus.reduce((mainMenus, mainMenu) => {
    const subMenus = mainMenu.sub_menus.reduce((subMenus, subMenu) => {
      const subMenuInUse = subMenusShared.get(subMenu.sub_menu_id.toString());
      if (subMenuInUse) {
        subMenus.set(subMenu.sub_menu_id.toString(), subMenuInUse);
      }
      return subMenus;
    }, new Map());

    return mainMenus;
  }, mainMenuInUseMap);
};

export const isAdminSignIn = (path: string) => path.startsWith(`/signin/admin/`, 3);

export enum DICT_TYPE {
  BOT,
  BIZ,
  CUSTOM,
}

export enum CUSTOM_DICT_STATE {
  SUCCESS = "success",
  IN_PROGRESS = "in_progress",
  ROLLBACK = "rollback",
}

// basically a hardcoded listing of menus, where "branch" types offer a subselection, and "stem" types are basically just a button
export const loadHeaderMenus = (tenant: Tenant): HeaderMenu[] => {
  if (tenant.tenant_type === "hotel" && !tenant.code.startsWith("sandbox_")) {
    // code is the route that the user will be sent to, label is the dictCd of the menu text name
    return [
      {
        type: "branch",
        code: "hotel_admin_function",
        label: "biz_admin_header_menu_function",
        menus: [
          {
            code: "hotel_admin_function",
            hasDisableCondition: true,
            label: "biz_admin_header_menu_menu_setting",
          },
          {
            code: "hotel_admin_design",
            label: "biz_admin_header_menu_design",
          },
          {
            code: "hotel_admin_system_message",
            label: "biz_admin_header_menu_system_message",
          },
          {
            code: "hotel_admin_restriction",
            label: "biz_admin_header_menu_restriction",
          },
          // {
          //   code: "hotel_admin_ai",
          //   label: "biz_admin_header_menu_ai"
          // }
        ],
        codes: ["hotel_admin_function", "hotel_admin_design"],
      },
      {
        type: "stem",
        code: "hotel_admin_room",
        label: "biz_admin_header_menu_room",
      },
      {
        type: "branch",
        label: "biz_admin_header_menu_member",
        menus: [
          {
            code: "hotel_admin_group",
            label: "biz_admin_header_menu_group",
          },
          {
            code: "hotel_admin_member",
            label: "biz_admin_header_member",
          },
          {
            code: "hotel_admin_responder",
            label: "biz_admin_header_responder",
          },
          {
            code: "hotel_admin_admin_staff",
            label: "biz_admin_header_admin_staff_setting",
          },
          {
            code: "hotel_admin_login_status",
            label: "biz_admin_header_admin_login_status",
          },
        ],
        codes: ["hotel_admin_group", "hotel_admin_member", "hotel_admin_admin_staff", "hotel_admin_login_status"],
      },
      {
        type: "branch",
        // code: "user_dictionary",
        label: "translation",
        menus: [
          {
            code: "unique_words_setting",
            hasDisableCondition: true,
            label: "unique_words_setting",
          },
          {
            code: "user_dictionary",
            hasDisableCondition: true,
            label: "translation_result_edit",
          },
          {
            code: "import_dictionary",
            label: "import",
          },
        ],
        codes: ["unique_words_setting", "translation_result_edit", "import_dictionary"],
      },
      {
        type: "branch",
        label: "common_history",
        menus: [
          {
            code: "hotel_admin_inquiry_history",
            label: "hotel_admin_inquiry_history",
          },
          {
            code: "admin-operation-history",
            label: "admin-operation-history",
          },
        ],
        codes: ["operation", "hotel_admin_inquiry_history"],
      },
    ];
  } else if (tenant.tenant_type === "hotel") {
    return [
      {
        type: "branch",
        code: "hotel_admin_function",
        label: "biz_admin_header_menu_function",
        menus: [
          {
            code: "hotel_admin_function",
            label: "biz_admin_header_menu_menu_setting",
          },
          { code: "hotel_admin_design", label: "biz_admin_header_menu_design" },
          {
            code: "hotel_admin_system_message",
            label: "biz_admin_header_menu_system_message",
          },
          {
            code: "hotel_admin_restriction",
            label: "biz_admin_header_menu_restriction",
          },
        ],
        codes: ["hotel_admin_function", "hotel_admin_design"],
      },
      {
        type: "branch",
        // code: "user_dictionary",
        label: "translation",
        menus: [
          {
            code: "unique_words_setting",
            hasDisableCondition: true,
            label: "unique_words_setting",
          },
          {
            code: "user_dictionary",
            hasDisableCondition: true,
            label: "translation_result_edit",
          },
          {
            code: "import_dictionary",
            label: "import",
          },
        ],
        codes: ["unique_words_setting", "translation_result_edit", "import_dictionary"],
      },
      // { type: "stem", code: "conversations", label: "sandbox_conversations" },
      {
        type: "stem",
        code: "apply_to_production",
        label: "apply_to_production",
      },
    ];
  } else {
    return [
      {
        type: "stem",
        code: "hotel_admin_function",
        label: "biz_admin_header_menu_function",
      },
      {
        type: "stem",
        code: "cs_admin_design",
        label: "biz_admin_header_menu_design",
      },
      {
        type: "stem",
        code: "hotel_admin_member",
        label: "biz_admin_header_menu_member",
      },
      {
        type: "stem",
        code: "user_dictionary",
        label: "user_dictionary",
      },
    ];
  }
};

export const getSubMenuDetailPath = (tenant: Tenant, mainMenuId: number, subMenuId: number): string => {
  return tenant.tenant_type === "hotel"
    ? `/${i18n.locale}/hotel_admin_bot/${mainMenuId}/${subMenuId}`
    : `/${i18n.locale}/cs_admin_calendar/${mainMenuId}/${subMenuId}`;
};

export const findDictMessage = (dicts: QueryQcBizDict[]): string => {
  return (dicts.find((e) => e.lang_cd === i18n.locale) || { message: "" }).message;
};

export const makeSubMenuNameDictCds = (subMenus: QuerySubMenu[]): Set<string> => {
  const result = new Set<string>();
  for (const subMenu of subMenus) {
    const dictCds = getSubMenuNameDictCds(subMenu);
    if (dictCds) Array.from(dictCds).forEach((e) => result.add(e));
  }
  return result;
};

export const getSubMenuNameDictCds = (subMenu?: QuerySubMenu): Set<string> | null => {
  if (!subMenu || !subMenu.menu_option) return null;

  const result = new Set<string>();
  let obj = {};
  try {
    obj = JSON.parse(subMenu.menu_option);
  } catch (e) {
    console.error(e);
    return null;
  }
  if (!("title_dicts" in obj)) return null;

  //@ts-ignore
  obj["title_dicts"].forEach((e) => result.add(e));
  return result;
};
